// 材料信息提取：显示从材料里提取到的字段、来源和缺失 / 冲突，可手动填写或选定
import { useState } from "react";
import type { ExtractedField, ManifestSchema, TaskDTO } from "@csp/shared";
import { api, errorText } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLoad } from "../lib/hooks";
import { AddFieldDialog, schemaNeedsReread } from "./FileSchemaDialogs";
import { fmtTime } from "../lib/format";
import { Badge } from "./Badges";

const STATE_TEXT: Record<ExtractedField["state"], string> = { ok: "已提取", manual: "人工确定", missing: "缺失", conflict: "不一致" };
const STATE_TONE: Record<ExtractedField["state"], "green" | "amber" | "red" | "default"> = { ok: "green", manual: "default", missing: "amber", conflict: "red" };

/** 提取是否挡住开始：提取中、缺必填、有冲突 */
export function extractionBlocked(task: TaskDTO): string | null {
  const ex = task.extraction;
  if (ex?.status === "running") return "正在提取材料信息，提取完成后才能开始";
  if (task.missing.length) return `还缺：${task.missing.join("、")}`;
  return null;
}

export function ExtractionPanel({ task, canEdit, onChanged }: { task: TaskDTO; canEdit: boolean; onChanged: () => void }) {
  const ex = task.extraction;
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  // 管理员：删不需要的字段、手动加字段（手机号 / 邮箱这类材料里没有的）、让 AI 从材料再找一遍漏的
  const isAdmin = useAuth().user?.role === "admin";
  const typeQ = useLoad(() => (isAdmin ? api.type(task.typeId) : Promise.resolve(null)), [isAdmin, task.typeId, ex?.finishedAt]);
  const schema = typeQ.data?.manifestSchema as ManifestSchema | undefined;
  if (!ex) return null;
  const running = ex.status === "running";
  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(null);
    try { await fn(); setEditing(null); onChanged(); }
    catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };
  const save = (key: string, value: string | null) => act(() => api.setExtractedField(task.id, key, value));
  // 改字段定义（任务类型，所有任务生效），改完重新提取
  const saveSchema = async (next: ManifestSchema) => {
    const reread = schemaNeedsReread(schema, next);
    await api.updateType(task.typeId, { manifestSchema: next });
    await typeQ.reload();
    await api.extractTask(task.id, !reread).catch(() => undefined);
    onChanged();
  };
  const drop = (f: ExtractedField) => {
    if (!schema || !confirm(`「${f.label}」不需要，从这个类型的字段里删掉？`)) return;
    void act(() => saveSchema({ ...schema, version: schema.version + 1, fields: schema.fields.filter((x) => x.key !== f.key) }));
  };
  const lacking = ex.fields.filter((f) => f.state === "missing" && f.required);
  const conflicts = ex.fields.filter((f) => f.state === "conflict");

  return (
    <div className="card" data-testid="extraction-panel">
      <div className="row">
        <h2 style={{ margin: 0 }}>信息提取</h2>
        {running ? <Badge tone="amber">提取中…</Badge> : ex.status === "failed" ? <Badge tone="red">提取失败</Badge> : <Badge tone="green">已完成</Badge>}
        {ex.finishedAt && !running && <span className="muted small">{fmtTime(ex.finishedAt)} 完成</span>}
        <span className="spacer" />
        {canEdit && isAdmin && <button className="btn" disabled={busy || running} title="AI 把材料再读一遍，补上还没有的文件类别和字段" onClick={() => void act(() => api.discoverTask(task.id))} data-testid="rediscover">从材料再找一遍漏的</button>}
        {canEdit && isAdmin && schema && <button className="btn" disabled={busy || running} onClick={() => setAdding(true)}>＋ 添加字段</button>}
        {canEdit && <button className="btn" disabled={busy || running} onClick={() => void act(() => api.extractTask(task.id))}>重新提取</button>}
      </div>
      <p className="muted small">上传或删除材料后会自动重新提取。先按规则识别 Bizfile 等文字版文件，识别不到的字段交给 AI 读取。值不对可以直接修改。</p>
      <table className="tbl">
        <thead>
          <tr><th>字段</th><th>提取值</th><th>来源</th><th>状态</th>{canEdit && <th>操作</th>}</tr>
        </thead>
        <tbody>
          {ex.fields.map((f) => (
            <tr key={f.key} data-testid={`extract-${f.key}`}>
              <td className="nowrap">{f.label}{f.required && <span className="err"> *</span>}</td>
              <td>
                {editing === f.key ? (
                  <span className="row">
                    <input value={draft} aria-label={`填写 ${f.label}`} onChange={(e) => setDraft(e.target.value)} style={{ minWidth: 200 }} />
                    <button className="btn btn-primary" disabled={busy || !draft.trim()} onClick={() => void save(f.key, draft)}>保存</button>
                    <button className="btn" disabled={busy} onClick={() => setEditing(null)}>取消</button>
                  </span>
                ) : f.state === "conflict" ? (
                  <span className="row">
                    {uniqueValues(f).map((c) => (
                      <button key={c.value} className="btn" disabled={busy || !canEdit} title={`来自 ${c.fileName}`} onClick={() => void save(f.key, c.value)}>选用「{c.value}」</button>
                    ))}
                  </span>
                ) : (
                  <span translate="no">{f.value ?? <span className="muted">—</span>}</span>
                )}
              </td>
              <td className="small">{f.inputOnly && f.state !== "manual" ? "手动输入" : sourceText(f)}</td>
              <td><Badge tone={f.inputOnly && f.state === "missing" ? "default" : STATE_TONE[f.state]}>{f.inputOnly && f.state === "missing" ? "待填写" : STATE_TEXT[f.state]}</Badge></td>
              {canEdit && (
                <td className="nowrap">
                  {editing !== f.key && <button className="btn" disabled={busy || running} onClick={() => { setEditing(f.key); setDraft(f.value ?? ""); }}>{f.value ? "修改" : "填写"}</button>}
                  {f.state === "manual" && editing !== f.key && <button className="btn" disabled={busy || running} onClick={() => void save(f.key, null)}>改回提取值</button>}
                  {isAdmin && schema && editing !== f.key && <button className="btn" disabled={busy || running} title="这个字段用不上：从类型里删掉" onClick={() => drop(f)} aria-label={`不需要 ${f.label}`}>不需要</button>}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {ex.note && <p className="notice">{ex.note}</p>}
      {!running && (lacking.length > 0 || conflicts.length > 0) && (
        <p className="notice err">
          {lacking.length > 0 && <>缺少必填字段：{lacking.map((f) => f.label).join("、")}。请补传含这些信息的材料，或直接填写。</>}
          {conflicts.length > 0 && <> 多份材料不一致：{conflicts.map((f) => f.label).join("、")}，请选定一个值。</>}
        </p>
      )}
      {error && <p className="notice err">{error}</p>}
      {adding && schema && <AddFieldDialog schema={schema} onSave={saveSchema} onClose={() => setAdding(false)} />}
    </div>
  );
}

function uniqueValues(f: ExtractedField) {
  const seen = new Set<string>();
  return f.candidates.filter((c) => {
    const k = c.value.trim().replace(/\s+/g, " ").toUpperCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

function sourceText(f: ExtractedField): string {
  if (f.state === "manual") return "人工填写";
  if (f.state === "conflict") return f.candidates.map((c) => `${c.fileName}：${c.value}`).join("；");
  if (!f.source) return "—";
  return `${f.source.fileName}（${f.source.method === "ai" ? "AI" : "规则"}）`;
}
